// parent - SFUsageGraphData.tsx
import React, { useEffect, useState } from 'react'

import { DDSTypography } from 'den-design-system'
import PreferenceDropDown from '../../components/SalesforceAudit/PreferenceDropDown'
import SFUsageGraph from './SFUsageGraph'
import Spinner from '../../components/common/Spinner/Spinner'
import { getLastNDaysRange } from '../../utils/DateTimeUtils'
import { getSalesforceLimitUsage } from '../../api/salesforceAudit/salesforceAudit.service'
import { observer } from 'mobx-react-lite'
import pallete from '../../global/newPallete'
import { replaceUrl } from '../../utils/UrlUtils'
import routeConstants from '../../constants/RouteConstants'
import { useStoreContext } from '../../store/StoreContext'

interface UsageStats {
  total: number
  used: number
  available: number
  usagePercent: number
}

const SFUsageGraphData: React.FC = () => {
  const store = useStoreContext()
  const [preferenceTime, setPreferenceTime] = useState('7')
  const [usageData, setUsageData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  const salesforceApiNameMapping: Record<string, string> = {
    DailyApiRequests: 'API',
    DailyStreamingApiEvents: 'Streaming API Events',
    DailyBulkApiBatches: 'Bulk API batches',
    DailyFunctionsApiCallLimit: 'Functions API Calls',
    SingleEmail: 'Single Email'
  }

  const getApiNameFromUrl = (): string | null => {
    const url = location.pathname
    const parts = url.split('/usageData/')
    if (parts.length > 1) {
      return parts[1].split('/')[0]
    }
    return null
  }

  const salesforceApiName = getApiNameFromUrl()

  const apiLabel = salesforceApiName
    ? salesforceApiNameMapping[salesforceApiName] || salesforceApiName
    : 'API'

  useEffect(() => {
    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: 'Salesforce Audit',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_DASHBOARD,
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        )
      },
      {
        label: apiLabel,
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW + '/api',
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        )
      }
    ])
  }, [store])

  const preferenceOptions = [
    { label: 'Last 7 days', value: '7' },
    { label: 'Last 14 days', value: '14' },
    { label: 'Last 30 days', value: '30' }
  ]

  const handleAuditFrequencyChange = (value: string) => {
    setPreferenceTime(value)
  }

  useEffect(() => {
    const fetchUsageData = async () => {
      const salesforceApiName = getApiNameFromUrl()

      if (!salesforceApiName) {
        console.error('No API name found in URL')

        return
      }

      setLoading(true)

      try {
        const orgTimeZone =
          store.userStore.getOrganizationPreference()?.timeZone
        const dateRange = getLastNDaysRange(
          parseInt(preferenceTime),
          orgTimeZone
        )

        const id = store.salesforceAuditStore.auditData?.salesforceAuditId

        const payload = {
          id: id,
          salesforceApiName: salesforceApiName,
          paramsWithDateRange: dateRange
        }

        const response = await getSalesforceLimitUsage(payload)

        setUsageData(response.data)
      } catch (error) {
        console.error('Error fetching usage data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchUsageData()
  }, [preferenceTime, store])

  return (
    <>
      {loading ? (
        <Spinner />
      ) : (
        <div className='flex flex-col w-full py-[16px]  space-y-[12px]'>
          <div className='flex items-center justify-between'>
            <div>
              <DDSTypography.Title
                type='h3'
                variant='semiBold'
                color={pallete.colors.textDark3}
              >
                API
              </DDSTypography.Title>
              <DDSTypography.Paragraph
                size='para'
                variant='medium'
                color={pallete.colors.textDark6}
              >
                API usage resets every 24hrs
              </DDSTypography.Paragraph>
            </div>
            <div
              className='flex items-center rounded-[6px] h-[32px]'
              style={{
                border: `1px solid ${pallete.colors.neuButtonStokeDis}`
              }}
            >
              <PreferenceDropDown
                options={preferenceOptions}
                value={preferenceTime}
                onChange={handleAuditFrequencyChange}
                placeholder='Select Range'
                dropdownWidth='126px'
              />
            </div>
          </div>

          <SFUsageGraph data={usageData} days={parseInt(preferenceTime, 10)} />
        </div>
      )}
    </>
  )
}

export default observer(SFUsageGraphData)
